import React from "react";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import Skeleton from "@mui/material/Skeleton";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import OngsClient from "~/single-page/api/OngsClient";
import { Box, Typography } from "@mui/material";
import NoItemsMessage from "~/components/NoItemsMessage";
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '~/single-page/constants/routes';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Ícone customizado usando map_pin.png
const customIcon = L.icon({
  iconUrl: '/images/map_pin.png', // Usando o map_pin.png personalizado
  iconRetinaUrl: '/images/map_pin@2x.png',
  // shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [42, 50], // Tamanho ajustado para o pin customizado
  iconAnchor: [16, 40], // Ponto de ancoragem no centro inferior
  popupAnchor: [0, -40], // Posição do popup relativa ao ícone
  // shadowSize: [41, 41]
});

const NGOsMapNew = ({ filteredActions = [], filteredCauses = [], searchTerm = '' }) => {
  const navigate = useNavigate();
  
  const {
    data: allNgosLocations = [],
    pending,
    success,
    error
  } = useApiCall({
    apiCall: OngsClient.locations,
    init: true,
    onSuccess: (data) => {
      console.log('Dados recebidos com sucesso:', data);
    },
    onError: (err) => {
      console.error('Erro na requisição:', err);
    }
  });

  // Filtra as ONGs baseado nas ações ou causas filtradas
  const ngosLocations = React.useMemo(() => {
    // Se não há termo de busca, mostra todas as ONGs
    if (!searchTerm) {
      return allNgosLocations;
    }

    let filteredOngNames = new Set();

    // Se temos ações filtradas (página de ações)
    if (filteredActions.length > 0) {
      filteredActions
        .filter(action => action.ong && action.ong.name)
        .forEach(action => filteredOngNames.add(action.ong.name));
    }

    // Se temos causas filtradas (página de causas)
    if (filteredCauses.length > 0) {
      filteredCauses
        .filter(cause => cause.ongs && Array.isArray(cause.ongs))
        .forEach(cause => {
          cause.ongs.forEach(ong => {
            if (ong && ong.name) {
              filteredOngNames.add(ong.name);
            }
          });
        });
    }

    // Se não há dados filtrados, mostra todas
    if (filteredOngNames.size === 0) {
      return allNgosLocations;
    }

    // Retorna apenas as localizações das ONGs filtradas
    return allNgosLocations.filter(location => {
      return filteredOngNames.has(location.key);
    });
  }, [allNgosLocations, filteredActions, filteredCauses, searchTerm]);

  const defaultPosition = [-23.5505, -46.6333]; // São Paulo

  const renderMap = () => {
    if (pending) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={484}
          animation="wave"
          sx={{ borderRadius: 2 }}
        />
      );
    }

    if (!success || error) {
      return <NoItemsMessage itemName="ação ou projeto" gender="feminino" />;
    }

    if (!ngosLocations?.length) {
      const message = searchTerm 
        ? `Nenhuma ONG encontrada relacionada a "${searchTerm}"`
        : "Nenhuma ONG com localização disponível";
      
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: 484,
          backgroundColor: '#f5f5f5',
          borderRadius: 2
        }}>
          <Typography variant="h6" color="text.secondary" textAlign="center">
            {message}
          </Typography>
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          width: "100%",
          height: 484,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' // Sombra sutil para dar profundidade
        }}
      >
        <MapContainer 
          center={ngosLocations[0]?.location || defaultPosition}
          zoom={12}
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {ngosLocations.map((item, index) => (
            <Marker 
              key={item.id || item.key || index}
              position={[item.location.lat, item.location.lng]}
              icon={customIcon}
            >
              <Popup>
                <Box
                  onClick={() => item.id && navigate(`${APP_ROUTES.NGO}/${item.id}`)}
                  sx={{
                    cursor: item.id ? 'pointer' : 'default',
                    '&:hover': item.id ? {
                      '& .MuiTypography-root': {
                        color: '#009FE3',
                        textDecoration: 'underline'
                      }
                    } : {}
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 'bold',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {item.key}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Título com estilo similar ao exemplo fornecido */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 6, lg: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 'bold',
            fontSize: { 
              xs: '1.3rem',  // 460px e menor
              sm: '1.6rem',  // 600px+
              md: '1.9rem',  // 900px+
              lg: '2.2rem'   // 1200px+
            },
            color: '#2E2F32',
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            display: { xs: 'block', sm: 'flex' },
            alignItems: { sm: 'center' },
            justifyContent: { sm: 'center' },
            gap: { sm: 1 },
            lineHeight: { xs: 1.2, sm: 1.3 },
            textAlign: 'center'
          }}
        >
          {/* Primeira linha em mobile - Logo + Encontre + no mapa ações */}
          <Box 
            sx={{ 
              display: { xs: 'flex', sm: 'contents' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 0.5, sm: 1 },
              mb: { xs: 0.5, sm: 0 }
            }}
          >
            {/* Logo ao lado de "Encontre" */}
            <Box
              component="img"
              src="/images/moving_actions/bora_impactar2.png"
              alt="Logo Bora Impactar"
              sx={{
                width: { 
                  xs: '24px',  // 460px e menor
                  sm: '30px',  // 600px+
                  md: '40px',  // 900px+
                  lg: '52px'   // 1200px+
                },
                height: 'auto',
                marginTop: { xs: '-12px', sm: '-14px', md: '-16px' },
                flexShrink: 0
              }}
            />
            <Box
              component="span"
              sx={{
                color: '#009FE3',
                position: 'relative',
                display: 'inline-block',
                minWidth: 'fit-content'
              }}
            >
              Encontre
              <Box
                component="img"
                src="/images/moving_actions/blue_underline.png"
                alt="Underline decorativo"
                sx={{
                  position: 'absolute',
                  bottom: { xs: '-8px', sm: '-10px' },
                  left: { xs: '-20px', sm: '-25px', md: '-29px' },
                  width: { xs: '130%', sm: '135%', md: '138%' },
                  height: 'auto',
                  maxHeight: { xs: '10px', sm: '11px', md: '13px' },
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'contents' } }}>
              no mapa {filteredCauses.length > 0 ? 'causas' : 'ações'}
            </Box>
          </Box>
          
          {/* Segunda linha em mobile - e projetos centralizado */}
          <Box 
            component="span" 
            sx={{ 
              display: { xs: 'block', sm: 'inline' },
              textAlign: 'center'
            }}
          >
            e projetos
          </Box>
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            color: '#000000',
            opacity: 0.9,
            fontSize: { 
              xs: '0.85rem',  // 460px e menor
              sm: '0.95rem',  // 600px+
              md: '1.05rem',  // 900px+
              lg: '1.1rem'    // 1200px+
            },
            lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
            maxWidth: { xs: '320px', sm: '500px', md: '700px', lg: '800px' },
            mx: 'auto',
            textAlign: 'center',
            px: { xs: 1, sm: 2 }
          }}
        >
          Explore no mapa quem já está fazendo a diferença e junte-se a essa mudança.
        </Typography>
      </Box>

      {/* Mapa */}
      {renderMap()}
    </Box>
  );
};

export default NGOsMapNew;